import { evaluateConditional } from '../services/mbrs/conditional/conditional-field.evaluator';
import { evaluateRole5RequiredIfAllVisibleDirectorsMatch } from '../services/mbrs/validators/role5-statement-by-directors.rules';

/**
 * THE single answer to "does this control's label get a `*`?".
 *
 * ## Why this exists
 *
 * The `*` was never a rule the app applied — it was markup copied from sheet to
 * sheet. Nine sheets never received it, and each of the nine that did hand-rolled
 * its own `isFieldRequired`, understanding only the declaration forms its own
 * schema happened to use. Move a mechanism between sheets and the marker silently
 * stops appearing. Design rules: `.claude/rules/required-marker.md`.
 *
 * ## The rule (PO, confirmed 2026-08-11)
 *
 * A label carries a `*` when **the schema declares the field mandatory** —
 * REGARDLESS of whether an error can currently fire. A field whose mandatory
 * error is suppressed because it is disabled, readonly, or waiting on a parent
 * field still carries the `*`. This is why there is deliberately **no
 * `ui.readonly` / `ui.disabledWhen` exclusion** here, even though the pure
 * validators DO exclude those from the *error* set (see `deriveDisabledKeys` in
 * `filing-info-common.rules.ts`). Marking and erroring are different questions;
 * do not "align" them.
 *
 * ## The six declaration forms
 *
 * Unconditional:
 *  1. `required: true`
 *  2. `ui.required: true`                      — role 19 only, easy to miss
 *  3. `validators: [{ name: 'required' }]`     — the LB-001 fix-guard form
 *
 * Conditional:
 *  4. `requiredIf` — **polymorphic, and the main trap this util absorbs**:
 *       `{ qname, equals }`                 → same-sheet   (roles 0, 1)
 *       `{ sheet, field, equals }`          → cross-sheet  (role 12)
 *       `{ sheet, qname, equals }`          → cross-sheet  (role 24)
 *     Note the cross-sheet field name is spelled `field` on one sheet and
 *     `qname` on the other. Both are accepted.
 *  5. `requiredIfAny: [{ qname, equals }]`      — same-sheet, ANY match (roles 0, 1)
 *     `requiredIfCrossSheet: { sheet, field, equals }`  — role 20
 *     `requiredIfAllVisibleDirectorsMatch`             — role 5, delegated to
 *       `evaluateRole5RequiredIfAllVisibleDirectorsMatch` so the marker, the
 *       component's validators, and the pure validator share one implementation
 *       (including its load-bearing zero-directors guard — with no directors
 *       selected, "all visible directors match" is vacuously true and would
 *       wrongly mark the dependent fields).
 *     `requiredIfAnyOfGroupFilled: [qname, …]`         — role 5's "other person"
 *       trio. Filling ANY member makes every member mandatory, so a preparer who
 *       starts the group must finish it. Declared per control in the schema (not
 *       derived) so this util, the component and the pure validator can each read
 *       the group without being handed the whole schema. OR-ed with whatever else
 *       the control declares.
 *  6. `conditional: { controlledBy, activeWhenValueIn | inactiveWhenValueIn,
 *     appliesTo }` — role 7. Delegated to the pure `evaluateConditional`.
 *     `appliesTo: 'visibility'` never implies mandatory.
 *
 * A seventh form, `validators: [{ name: 'required', conditionalOn: {…} }]`, is
 * handled but currently has **no users in any of the 41 schemas** — role 20's
 * hand-rolled branch for it was already dead. Kept so a future schema edit is
 * picked up rather than silently unmarked.
 *
 * ## Resolving conditions
 *
 * Pass a {@link MandatoryContext}. Every lookup is optional: a condition whose
 * data is unavailable resolves to **not mandatory** rather than throwing, which
 * matches the pre-existing per-sheet behaviour (a cross-sheet rule whose sheet
 * was not loaded degrades to optional). Omit the context entirely and only the
 * three unconditional forms can fire.
 *
 * Angular-free and dependency-light on purpose, so the pure validators can adopt
 * it later without breaking `npm run check:validator-purity`.
 */
export interface MandatoryDerivableControl {
  qname?: string;
  required?: boolean;
  ui?: { required?: boolean; [k: string]: any };
  validators?: ReadonlyArray<{ name?: string; conditionalOn?: { qname?: string; values?: readonly any[] } }>;
  requiredIf?: { qname?: string; field?: string; sheet?: string; equals?: unknown };
  requiredIfAny?: ReadonlyArray<{ qname?: string; equals?: unknown }>;
  requiredIfCrossSheet?: { sheet?: string; field?: string; equals?: unknown };
  requiredIfAllVisibleDirectorsMatch?: {
    directorCountField: string;
    directorFields: ReadonlyArray<string>;
    equals: string;
  };
  requiredIfAnyOfGroupFilled?: ReadonlyArray<string>;
  conditional?: any;
  [k: string]: any;
}

export interface MandatoryContext {
  /** Current values for THIS sheet, keyed by qname. Drives the same-sheet forms. */
  selfData?: Record<string, any>;
  /** Look up a field on ANOTHER sheet. Drives every cross-sheet form. */
  crossSheetValue?: (sheet: string, field: string) => unknown;
  /** Filing-info values, for `conditional.controlledBy: { sheet: 'filingInfo' }`. */
  filingInfo?: Record<string, any>;
  /**
   * Optional visibility gate. When supplied and it returns false, the control is
   * reported NOT mandatory. Sheets that render hidden controls (role 4, role 5)
   * pass their `shouldShowField` here so an off-screen director slot is not
   * marked. Sheets whose templates already guard visibility need not pass it.
   */
  isVisible?: (control: MandatoryDerivableControl) => boolean;
}

/** The three unconditional forms. Exported for schema-invariant specs. */
export function isUnconditionallyMandatory(control: MandatoryDerivableControl | null | undefined): boolean {
  if (!control) return false;
  if (control.required === true) return true;
  if (control.ui?.required === true) return true;
  return (control.validators ?? []).some(
    (validator) => validator?.name === 'required' && validator?.conditionalOn === undefined
  );
}

function readSelf(ctx: MandatoryContext, qname: string | undefined): unknown {
  if (!qname) return undefined;
  return ctx.selfData?.[qname];
}

/** `requiredIf` is polymorphic — see form 4 in the class docblock. */
function requiredIfActive(
  rule: NonNullable<MandatoryDerivableControl['requiredIf']>,
  ctx: MandatoryContext
): boolean {
  if (rule.sheet) {
    // Cross-sheet. Role 12 spells the target `field`, role 24 spells it `qname`.
    const field = rule.field ?? rule.qname;
    if (!field || !ctx.crossSheetValue) return false;
    return ctx.crossSheetValue(rule.sheet, field) === rule.equals;
  }
  return readSelf(ctx, rule.qname) === rule.equals;
}

export function isControlMandatory(
  control: MandatoryDerivableControl | null | undefined,
  ctx: MandatoryContext = {}
): boolean {
  if (!control) return false;
  if (ctx.isVisible && !ctx.isVisible(control)) return false;

  if (isUnconditionallyMandatory(control)) return true;

  // Conditional `validators` entry (no users today — see docblock).
  const conditionalRequired = (control.validators ?? []).find(
    (validator) => validator?.name === 'required' && validator?.conditionalOn
  );
  if (conditionalRequired?.conditionalOn) {
    const { qname, values } = conditionalRequired.conditionalOn;
    if (Array.isArray(values) && values.includes(readSelf(ctx, qname))) return true;
  }

  if (control.requiredIf && requiredIfActive(control.requiredIf, ctx)) return true;

  if (
    Array.isArray(control.requiredIfAny) &&
    control.requiredIfAny.some((rule) => rule && readSelf(ctx, rule.qname) === rule.equals)
  ) {
    return true;
  }

  const cross = control.requiredIfCrossSheet;
  if (cross?.sheet && cross.field && ctx.crossSheetValue) {
    if (ctx.crossSheetValue(cross.sheet, cross.field) === cross.equals) return true;
  }

  const directors = control.requiredIfAllVisibleDirectorsMatch;
  if (directors) {
    if (evaluateRole5RequiredIfAllVisibleDirectorsMatch(ctx.selfData ?? {}, directors)) return true;
  }

  // Group form: filling ANY member of a declared group makes every member
  // mandatory. Whitespace is not a commitment, so a space-only entry reads as
  // empty — matching how `Validators.required` treats it.
  const group = control.requiredIfAnyOfGroupFilled;
  if (Array.isArray(group)) {
    const filled = group.some((qname) => {
      const value = readSelf(ctx, qname);
      return value !== null && value !== undefined && String(value).trim() !== '';
    });
    if (filled) return true;
  }

  // `appliesTo: 'visibility'` controls rendering only — it never implies mandatory.
  if (control.conditional && control.conditional.appliesTo !== 'visibility') {
    return evaluateConditional(control.conditional, ctx.selfData ?? {}, {
      filingInfo: ctx.filingInfo,
    }).validationActive;
  }

  return false;
}

/**
 * A control whose value the app computes — the preparer cannot type into it.
 *
 * Bracket access is required, not stylistic: `ui` carries an index signature so
 * `noPropertyAccessFromIndexSignature` rejects dot access on these two keys.
 */
export function isComputedControl(control: MandatoryDerivableControl | null | undefined): boolean {
  return control?.ui?.['calculated'] === true || control?.ui?.['readonly'] === true;
}

/**
 * May an EMPTY value on this control block submit?
 *
 * The companion to {@link isControlMandatory}, and the two are deliberately
 * different. Read them as two questions about the same field:
 *
 *   isControlMandatory(c)       →  "does the label get a `*`?"      (presentation)
 *   isMandatoryForValidation(c) →  "may empty block submit?"        (enforcement)
 *
 * The difference is **computed controls**. A calculated / readonly total is
 * populated by `CalculationService`, so an empty one means "nothing to total
 * yet" — never "the preparer skipped a field". Flagging it as an error is a
 * false positive by construction, because there is no input for the user to
 * correct.
 *
 * ## Developer Note — why a shared predicate and NOT a schema flag
 *
 * A `validation.requiredMarkerOnly: true` schema flag was considered for the
 * 2026-08-11 mandatory-totals change (108 calculated totals flagged mandatory
 * across 14 schemas, purely to light their `*`). It was rejected, and the
 * reasoning is recorded here because it is not obvious from the code alone:
 *
 *  1. **`required` + `calculated` is not new.** 32 of the 189 required controls
 *     already combined them BEFORE that change — every readonly field on roles
 *     0/1 (Type of submission, Taxonomy version, the Method fields), role 10's
 *     four `TOTAL` rows, and role 19's `Equity:End`, whose spec states outright
 *     that "the requirement is satisfied by the formula output, not by typing".
 *     A new flag would not retrofit those, leaving two patterns for one concept.
 *  2. **It would be a seventh way to declare mandatory-ness**, introduced
 *     immediately after five hand-rolled marker idioms were collapsed into one.
 *     Too many ways to say the same thing is the root cause this whole
 *     workstream removed; adding one back trades an enforceable rule for a
 *     declarative one.
 *  3. **It misstates intent.** The PO's ruling is that these fields ARE
 *     mandatory. "Marker-only" records them as decoration.
 *
 * ## Developer Note — the failure this prevents (role 15 is the worked example)
 *
 * Before this predicate existed, each sheet re-derived the skip locally, and
 * three sheets (8, 9, 15) did not have it at all while five (10, 12, 13, 18,
 * 19) did. **Role 15 shows why that is worse than it sounds.**
 *
 * Role 15's pure validator enforces required-ness ONLY for the two
 * conditionally-mandatory attribution rows (schema `requiredIf`) — never for a
 * calculated total. Its component, meanwhile, reads the flag inside
 * `_computeRowErrorFlags`:
 *
 *     const hasError = hasInvalid || (isRequired && !hasValue) || …
 *
 * Role 15 is on `PURE_VERDICT_SHEETS`, so the SHEET-level blocking verdict comes
 * from the pure validator. Flag a calculated total there without the skip and an
 * all-empty totals row paints **red with a "Failed" badge while the sheet tick
 * stays green and submit stays open** — the editor and the headless listing
 * disagreeing about the same data, which is exactly what the pure-verdict
 * convergence work exists to prevent.
 *
 * That is a harder bug than roles 8/9 would have produced (those feed both
 * surfaces, so they would at least have been consistently wrong), and it is
 * invisible to a green suite. Hence: one predicate, one place, named so the
 * choice between the two questions is explicit at every call site.
 *
 * **When you add a required-field check to a sheet, call THIS — not
 * `isControlMandatory`.** Use `isControlMandatory` only to drive the `*`.
 */
export function isMandatoryForValidation(
  control: MandatoryDerivableControl | null | undefined,
  ctx: MandatoryContext = {}
): boolean {
  if (isComputedControl(control)) return false;
  return isControlMandatory(control, ctx);
}
